import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Box, Chip, TextField, Typography, InputAdornment,
  Dialog, Checkbox, Tooltip
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const fallback = [
  { _id: "1", className: "Nilai 5 Tamil", subject: "Tamil Speaking", classroomUrl: "https://classroom.google.com", academicYear: "2024-25" },
  { _id: "2", className: "Nilai 3 Tamil", subject: "Tamil Writing", classroomUrl: "https://classroom.google.com", academicYear: "2024-25" },
  { _id: "3", className: "Nilai 7 Tamil", subject: "Tamil Reading", classroomUrl: "https://classroom.google.com", academicYear: "2024-25" },
];

const exportColumns = [
  { label: "Class", field: "className" },
  { label: "Subject", field: "subject" },
  { label: "Classroom URL", field: "classroomUrl" },
  { label: "Academic Year", field: "academicYear" },
];

function ClassRoom() {
  const [rawData, setRawData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadClassrooms = useCallback(async () => {
    try {
      const applicationId = sessionStorage.getItem("applicationId") || "";
      const url = applicationId ? `student/classroom/list?applicationId=${applicationId}` : "student/classroom/list";
      const response = await fetchData(url);
      if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        const mapped = response.data.map((r, i) => ({
          _id: String(r.id || i + 1),
          className: r.className || r.class || r.name || "—",
          subject: r.subject || r.subjectName || "—",
          classroomUrl: r.classroomUrl || r.url || "",
          academicYear: r.academicYear || r.year || "—",
          status: r.status || "Active",
        }));
        setRawData(mapped);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadClassrooms(); }, [loadClassrooms]);

  const handleOpenClassroom = (url) => { window.open(url || "https://classroom.google.com", "_blank"); };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredRaw = filterRows(rawData, filterText);

  const allSelected = filteredRaw.length > 0 && filteredRaw.every((r) => selectedIds.has(r._id));
  const someSelected = !allSelected && filteredRaw.some((r) => selectedIds.has(r._id));

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRaw.map((r) => r._id)));
  };

  const SelectAllHeader = (
    <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        indeterminateIcon={<IndeterminateCheckBoxRoundedIcon sx={{ color: "#4caf50", fontSize: "20px" }} />}
        sx={{ padding: "2px" }}
      />
    </Tooltip>
  );

  const handleExport = () => exportToCSV(exportColumns, filteredRaw, "google_classroom.csv");

  const handleDeleteConfirm = () => {
    setRawData([]);
    setSelectedIds(new Set());
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
    openButton: { backgroundColor: "#4caf50", color: "#fff", fontSize: "12px", textTransform: "none", padding: "6px 16px", borderRadius: "6px", "&:hover": { backgroundColor: "#388e3c" } },
  };

  const tableColumns = [
    { label: SelectAllHeader, field: "checkbox" },
    { label: "Class", field: "className" },
    { label: "Subject", field: "subject" },
    { label: "Classroom URL", field: "classroomUrlDisplay" },
    { label: "Academic Year", field: "academicYear" },
    { label: "Action", field: "action" },
  ];

  const displayRows = filteredRaw.map((row) => ({
    ...row,
    checkbox: (
      <Checkbox
        checked={selectedIds.has(row._id)}
        onChange={() => handleToggleSelect(row._id)}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#D5D7DA" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#4caf50" }} />}
        sx={{ padding: "4px" }}
      />
    ),
    classroomUrlDisplay: row.classroomUrl ? (
      <a href={row.classroomUrl} target="_blank" rel="noopener noreferrer"
        style={{ color: "#2196f3", textDecoration: "underline", fontSize: "13px" }}>
        {row.classroomUrl}
      </a>
    ) : (
      <span style={{ color: "#8F95B2", fontSize: "13px" }}>—</span>
    ),
    action: row.status?.toLowerCase() === "inactive" ? (
      <Chip
        label="Unavailable"
        size="small"
        sx={{ backgroundColor: "#f5f5f5", color: "#9e9e9e", fontWeight: 600, fontSize: "12px" }}
      />
    ) : (
      <Button sx={styles.openButton} startIcon={<OpenInNewIcon sx={{ fontSize: "14px" }} />} onClick={() => handleOpenClassroom(row.classroomUrl)}>
        Open Classroom
      </Button>
    ),
  }));

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Google Classroom
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Google Classroom</h2>
          <Chip label={`${filteredRaw.length} Enrolled`} sx={styles.badge} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: showFilter ? 1 : 2, flexWrap: "wrap" }}>
        <Button startIcon={<DeleteOutlineIcon />} sx={styles.actionBtn} onClick={() => setDeleteConfirm(true)}>Delete</Button>
        <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
        <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by class, subject, academic year..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <DynamicTable columns={tableColumns} rows={displayRows} />
    </div>
  );
}

export default ClassRoom;
